import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import UnifiedCheckout from '../components/UnifiedCheckout';
import {
  CheckCircle2,
  ArrowRight,
  Download,
  FileText,
  Mail,
  CreditCard,
  Award,
  Users,
  ExternalLink,
  Loader2,
  ShoppingCart,
  Star
} from 'lucide-react';

const AfiliacionPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/checkout/products');
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los planes de afiliación",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (product) => {
    if (!isAuthenticated) {
      toast({
        title: "Inicia sesión",
        description: "Debes iniciar sesión para realizar la compra",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }
    setSelectedProduct({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      features: product.features || []
    });
    setCheckoutOpen(true);
  };

  const handleCheckoutSuccess = (result) => {
    toast({
      title: "¡Éxito!",
      description: result.free ? "Acceso otorgado" : "Pago procesado correctamente"
    });
    setCheckoutOpen(false);
  };

  const steps = [
    {
      number: 1,
      title: 'Realiza el pago',
      description: 'Selecciona tu plan y realiza el pago de tu afiliación de forma segura.',
      icon: CreditCard
    },
    {
      number: 2,
      title: 'Envía documentos',
      description: 'Envía los documentos requeridos a info@upim.org',
      icon: Mail
    },
    {
      number: 3,
      title: 'Recibe tu credencial',
      description: 'Te avisaremos de tu afiliación y recibirás tu credencial digital.',
      icon: Award
    }
  ];

  const requiredDocuments = [
    'PDF de tu Identificación oficial (INE o pasaporte)',
    'Comprobante de domicilio',
    'Código de Ética firmado y escaneado',
    'Manual de Afiliación firmado'
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              Afíliate a UPIM
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              ¡Estás a unos pasos de pertenecer a la mejor Asociación!
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Conviértete en miembro de la Unión de Profesionales Inmobiliarios de México 
              y accede a un mundo de beneficios exclusivos.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle2 className="h-5 w-5 text-gray-700" />
                <span>Asesorías personalizadas</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle2 className="h-5 w-5 text-gray-700" />
                <span>Descuentos exclusivos</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <CheckCircle2 className="h-5 w-5 text-gray-700" />
                <span>Eventos de formación</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Affiliate */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              ¿Cómo puedo afiliarme?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Sigue estos sencillos pasos para convertirte en afiliado UPIM.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <step.icon className="h-8 w-8 text-gray-700" />
                  </div>
                  <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gray-200 hidden md:block last:hidden" 
                       style={{ display: index === 2 ? 'none' : undefined }} />
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-900 text-white text-sm font-bold mb-4">
                    {step.number}
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Required Documents */}
          <Card className="max-w-2xl mx-auto border-gray-200 bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="h-5 w-5 mr-2 text-gray-700" />
                Documentos requeridos
              </CardTitle>
              <CardDescription>
                Envía los siguientes documentos en PDF a <strong>info@upim.org</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {requiredDocuments.map((doc, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{doc}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button variant="outline" size="sm" asChild>
                  <a href="https://upim.org/wp-content/uploads/2023/10/CODIGO-HONOR-Y-JUSTICIA-UPIM.doc.pdf" target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Código de Ética
                  </a>
                </Button>
                <Button variant="outline" size="sm" asChild>
                  <a href="https://upim.org/wp-content/uploads/2023/10/MANUAL-DE-AFILIACION-UPIM-2023.pdf" target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4 mr-2" />
                    Manual de Afiliación
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              Planes de Afiliación
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Costos de Afiliación
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tus necesidades. Pago seguro con Stripe.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => {
                const isPopular = product.id === 'membresia-titular';
                const isCertification = product.type === 'certification';
                
                return (
                  <Card 
                    key={product.id} 
                    className={`relative overflow-hidden transition-all duration-300 ${
                      isPopular 
                        ? 'border-2 border-gray-900 shadow-lg scale-105' 
                        : 'border border-gray-200 hover:shadow-lg'
                    }`}
                    data-testid={`plan-card-${product.id}`}
                  >
                    {isPopular && (
                      <div className="absolute top-0 right-0 bg-gray-900 text-white text-xs font-bold px-3 py-1 flex items-center">
                        <Star className="h-3 w-3 mr-1" />
                        Más Popular
                      </div>
                    )}
                    {isCertification && (
                      <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-3 py-1 flex items-center">
                        <Award className="h-3 w-3 mr-1" />
                        Certificación
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="text-xl">{product.name}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-6">
                        <span className="text-4xl font-bold text-gray-900">
                          ${product.price?.toLocaleString()}
                        </span>
                        <span className="text-gray-500 text-sm"> {product.currency || 'MXN'}</span>
                      </div>
                      
                      {product.features && product.features.length > 0 && (
                        <ul className="space-y-3 mb-6">
                          {product.features.map((feature, index) => (
                            <li key={index} className="flex items-start space-x-3">
                              <CheckCircle2 className="h-5 w-5 text-gray-700 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-600 text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <Button 
                        className={`w-full ${
                          isPopular 
                            ? 'bg-gray-900 hover:bg-gray-800' 
                            : 'bg-gray-800 hover:bg-gray-700'
                        }`}
                        onClick={() => handleSelectPlan(product)}
                        data-testid={`select-plan-${product.id}`}
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Seleccionar Plan
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Certification Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Certificación Oficial
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Certificación CONOCER EC0110.02
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                <strong>Asesoría en comercialización de bienes inmuebles</strong>: 
                Certifícate oficialmente como profesional inmobiliario con el estándar 
                de competencia EC0110.02 avalado por CONOCER.
              </p>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-4">Incluye:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-gray-700" />
                    <span className="text-gray-600">8 horas de Taller de Alineación (2 días, 4 horas por día)</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-gray-700" />
                    <span className="text-gray-600">Evaluación (3er día, 3 horas)</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-gray-700" />
                    <span className="text-gray-600">Certificado oficial CONOCER</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-100 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">Afiliados</p>
                  <p className="text-2xl font-bold text-gray-900">$3,479</p>
                  <p className="text-xs text-gray-500">+ IVA</p>
                </div>
                <div className="bg-gray-100 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-600 mb-1">No Afiliados</p>
                  <p className="text-2xl font-bold text-gray-700">$4,639</p>
                  <p className="text-xs text-gray-500">+ IVA</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button className="bg-gray-900 hover:bg-gray-800" asChild>
                  <Link to="/academia">
                    Inscribirme
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <a href="https://wa.me/message/CDJFP72PTKLSH1" target="_blank" rel="noopener noreferrer">
                    Informes por WhatsApp
                  </a>
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&h=450&fit=crop"
                alt="Certificación CONOCER"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 hidden md:block border border-gray-100">
                <div className="flex items-center space-x-3">
                  <Award className="h-10 w-10 text-gray-700" />
                  <div>
                    <p className="font-bold text-gray-900">EC0110.02</p>
                    <p className="text-sm text-gray-500">Certificación CONOCER</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 lg:py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            ¿Tienes dudas sobre la afiliación?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Contáctanos a través de nuestros diferentes medios o escríbenos por WhatsApp.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 px-8 h-12"
              asChild
            >
              <a href="https://wa.me/message/CDJFP72PTKLSH1" target="_blank" rel="noopener noreferrer">
                Escribir por WhatsApp
                <ExternalLink className="ml-2 h-5 w-5" />
              </a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-gray-600 text-white hover:bg-gray-800 px-8 h-12"
              asChild
            >
              <Link to="/contacto">
                Contactar a UPIM
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Unified Checkout Modal */}
      <UnifiedCheckout
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        product={selectedProduct}
        onSuccess={handleCheckoutSuccess}
        successUrl={`${window.location.origin}/pago-exitoso`}
        cancelUrl={`${window.location.origin}/afiliacion`}
      />
    </Layout>
  );
};

export default AfiliacionPage;
